import React, { useState, useRef, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { useRouter } from 'next/router'
import cn from 'classnames'

import { Availability } from 'src/pages/api/trpc/availability/schema'

import Room from 'client/components/Rooms/RoomResponsive'

import { getRoomsByName } from './utils'
import { SHOW_MORE_LIMIT } from './constants'
import styles from './styles.module.css'

import ShowMore from 'client/components/Rooms/RoomResponsive/ShowMore'
import { useDevice } from 'src/hooks/useDevice'
import { BreakpointSizes } from 'src/types/device'

export type Board = Availability['boards'][0]

interface IRoomsCollection {
  closeGroup: () => void
  isOpen: boolean
  rooms: Board[]
  withHeader?: boolean
}

const RoomsCollection = ({
  isOpen,
  closeGroup,
  rooms = [],
  withHeader = false,
}: IRoomsCollection) => {
  const router = useRouter()
  const roomsRef = useRef(null)

  const roomsSorted = useMemo(() => getRoomsByName(rooms), [rooms])

  const { total, price } = router.query

  const priceQuery = parseInt(total as string, 10) || parseInt(price as string, 10)

  const affiliateBoard =
    roomsSorted.flat().find(board => priceQuery === board.total)?.availabilityId ?? ''

  const totalRooms = roomsSorted.length

  const showMoreLimit = SHOW_MORE_LIMIT

  const [visibleRoomsCount, setVisibleRoomsCount] = useState(showMoreLimit)

  const incrementVisibleRooms = () => {
    setVisibleRoomsCount(prevCount => prevCount + showMoreLimit)
  }

  const roomsToDisplay = useMemo(
    () => roomsSorted.slice(0, visibleRoomsCount),
    [roomsSorted, visibleRoomsCount]
  )

  const moreRooms = totalRooms - visibleRoomsCount

  const { activeBreakpoint } = useDevice()
  const isMobile = activeBreakpoint < BreakpointSizes.M

  const getShowMoreComponent = () => {
    return (
      <div className={styles.showMoreRoomsContainer}>
        <ShowMore onClick={incrementVisibleRooms} outlinedShowMoreButton={isMobile}>
          <FormattedMessage id="global.show-more-rooms-from" values={{ rooms: moreRooms }} />
        </ShowMore>
      </div>
    )
  }

  return (
    <div
      className={cn(
        styles.roomCollection,
        { [styles.hidden]: !isOpen },
        { [styles.withHeader]: isOpen && withHeader }
      )}
    >
      <ul data-testid="rooms-list" ref={roomsRef}>
        {roomsToDisplay.map((room, index) => {
          return (
            <li
              key={`${room[0]?.roomUniqueId}-${room[0].roomIndex}`}
              className={styles.item}
              data-testid="rooms-list-item"
              data-mapped={!!room[0].normalizedRoomCode}
            >
              <Room
                room={room}
                closeGroup={closeGroup}
                affiliateBoard={affiliateBoard}
                topRef={roomsRef}
                initialOpenOffers={index === 0}
              />
            </li>
          )
        })}
      </ul>

      {moreRooms > 0 && getShowMoreComponent()}
    </div>
  )
}

export default RoomsCollection
